/**
 * SaveStatusBadge — Indicateur discret d'état de sauvegarde du brouillon.
 * Phase 3h — Bugs 9 & 10.
 *
 * Affiche selon `status` :
 *  - idle → "Non enregistré" (attente debounce, ambre subtil)
 *  - saving → "Enregistrement…" (spinner)
 *  - saved → "Enregistré ✓ · il y a Xs" (vert olive, mise à jour temps relatif)
 *  - error → "Erreur d'enregistrement" (rouge)
 */
import { useEffect, useState } from "react";
import { Check, Loader2, AlertTriangle, Circle } from "lucide-react";

function timeAgoFR(from) {
  if (!from) return "";
  const s = Math.max(0, Math.floor((Date.now() - from.getTime()) / 1000));
  if (s < 5) return "à l'instant";
  if (s < 60) return `il y a ${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h} h`;
}

export default function SaveStatusBadge({ status, lastSavedAt, isDirty, onRetry }) {
  const [tick, setTick] = useState(0);
  // Refresh label toutes les 15s pour "il y a Xs"
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);
  // Force re-render dep on tick (silence lint)
  void tick;

  if (status === "saving") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-slate-800/60 px-2 py-0.5 text-[11px] text-slate-300"
        data-testid="save-status-badge"
        data-status="saving"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Enregistrement…
      </span>
    );
  }
  if (status === "error") {
    const handleRetry = () => {
      // Le hook re-set status="error" en interne, on catch ici pour éviter Uncaught runtime error
      try {
        const p = onRetry?.();
        if (p && typeof p.catch === "function") {
          p.catch((err) => {
            console.warn("[SaveStatusBadge] Retry failed silently:", err);
          });
        }
      } catch (_e) {
        /* silencieux — le badge affichera à nouveau l'erreur */
      }
    };
    return (
      <button
        onClick={handleRetry}
        className="inline-flex items-center gap-1 rounded bg-red-900/40 px-2 py-0.5 text-[11px] text-red-300 hover:bg-red-900/70"
        data-testid="save-status-badge"
        data-status="error"
        title="Cliquer pour réessayer l'enregistrement"
      >
        <AlertTriangle className="h-3 w-3" /> Erreur — Réessayer
      </button>
    );
  }
  if (isDirty || status === "idle") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-[#D99A2B]/15 px-2 py-0.5 text-[11px] text-[#F0C572]"
        data-testid="save-status-badge"
        data-status="dirty"
      >
        <Circle className="h-2.5 w-2.5 fill-current" /> Non enregistré
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-[#5A6338]/25 px-2 py-0.5 text-[11px] text-[#C4D68A]"
      data-testid="save-status-badge"
      data-status="saved"
    >
      <Check className="h-3 w-3" /> Enregistré · {timeAgoFR(lastSavedAt)}
    </span>
  );
}
